import { useCallback, useMemo, useState } from "react";
import type { InsufficientCreditsError, Shipment } from "api";
import cn from "classnames";
import { OutOfCreditsModal } from "components/Billing/OutOfCreditsModal";
import { Modal } from "components/ui/Modal";
import { PRODUCTS_URL, QUOTE_URL } from "constants/common.constants";
import { EMPTY_STATE_CUBE } from "constants/products.constants";
import type { LoadStatus } from "hooks/useProfile";
import { AddShipmentModal } from "./AddShipmentModal";
import { ShipmentFilters } from "./ShipmentFilters";
import { ShipmentRow } from "./ShipmentRow";
import type { ShipmentRowAction } from "./ShipmentRowMenu";
import { ShipmentRowMenu } from "./ShipmentRowMenu";
import type { BucketFilter, ModeFilter } from "./shipments.model";
import { matchesFilters, trackingUrl } from "./shipments.model";

type MyShipmentsPanelProps = {
  shipments: Shipment[];
  status: LoadStatus;
  onRemove: (ids: number[]) => Promise<void>;
  onAdd: (shipment: Shipment) => void;
  /** Opens the shipment's own page, over this list. */
  onOpenShipment: (id: number) => void;
  onSessionLost: () => void;
  /** Sends the visitor to the pane that shows what the plan has left. */
  onChoosePlan: () => void;
  onBuyCredits: () => void;
};

/** The kebab, and the row it belongs to, while the menu is open. */
type OpenMenu = { shipment: Shipment; anchor: HTMLElement };

export const MyShipmentsPanel = ({
  shipments,
  status,
  onRemove,
  onAdd,
  onOpenShipment,
  onSessionLost,
  onChoosePlan,
  onBuyCredits,
}: MyShipmentsPanelProps) => {
  const [bucket, setBucket] = useState<BucketFilter>("all");
  const [mode, setMode] = useState<ModeFilter>("all");
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  /** The ids a confirmed removal would take, or null while nothing is asked. */
  const [confirming, setConfirming] = useState<number[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [creditDenial, setCreditDenial] = useState<InsufficientCreditsError | null>(null);
  const [menu, setMenu] = useState<OpenMenu | null>(null);

  const visible = useMemo(
    () => shipments.filter((shipment) => matchesFilters(shipment, bucket, mode)),
    [shipments, bucket, mode]
  );

  // Rows the filter hides must not be swept up by "select all" or by delete.
  const pickedVisible = visible.filter((shipment) => picked.has(shipment.id));
  const allPicked = visible.length > 0 && pickedVisible.length === visible.length;

  const stopPicking = () => {
    setPicking(false);
    setPicked(new Set());
  };

  const pick = (id: number, on: boolean) => {
    setPicked((current) => {
      const next = new Set(current);

      if (on) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  };

  const pickAll = (on: boolean) => {
    setPicked((current) => {
      const next = new Set(current);

      visible.forEach((shipment) => (on ? next.add(shipment.id) : next.delete(shipment.id)));

      return next;
    });
  };

  const confirmRemove = async () => {
    const ids = confirming ?? [];

    setConfirming(null);
    await onRemove(ids);
    stopPicking();
  };

  /**
   * Opening one shipment. Every route into one (a row click, the kebab's
   * "Open shipment") comes through here and lands on its detail page — a card
   * with no number yet too, since the page is keyed by id, not by number.
   */
  const openShipment = useCallback((shipment: Shipment) => onOpenShipment(shipment.id), [onOpenShipment]);

  const closeMenu = useCallback(() => setMenu(null), []);

  const runAction = (action: ShipmentRowAction, shipment: Shipment) => {
    setMenu(null);

    if (action === "open") {
      openShipment(shipment);
      return;
    }

    if (action === "tracking") {
      const url = trackingUrl(shipment);

      if (url !== null) window.location.assign(url);

      return;
    }

    setConfirming([shipment.id]);
  };

  return (
    <div className="pcx">
      <div className="pcx__top">
        <div>
          <div className="pcx__h">My Shipments</div>
          <p className="pcx__sub">Everything your company is tracking, sea and air.</p>
        </div>

        {status === "ready" && (
          <div className="pcx__acts">
            {/* Nothing to select while the list is empty — the design hides it too. */}
            {shipments.length > 0 && (
              <button
                type="button"
                className="pcbtn pcsh__pickmode"
                onClick={() => (picking ? stopPicking() : setPicking(true))}
              >
                {picking ? "Cancel" : "Select to delete"}
              </button>
            )}
            <button type="button" className="pcbtn pcbtn--main pcsh__add" onClick={() => setAdding(true)}>
              Add shipment
            </button>
          </div>
        )}
      </div>

      {status === "loading" && <span className="pc-spin" aria-label="Loading" />}

      {status === "failed" && (
        <p className="pc-note pc-note--bad">Could not load your shipments. Please refresh the page.</p>
      )}

      {status === "unverified" && <p className="pc-note">Verify your email address to see your shipments.</p>}

      {status === "ready" && shipments.length === 0 && (
        <div className="pc-empty">
          <img className="pc-empty__cube" src={EMPTY_STATE_CUBE} alt="" width={96} height={96} />
          <div className="pc-empty__h">No shipments tracked yet</div>
          <p className="pc-empty__t">
            Bookings appear here as soon as a tracking product is connected. Every booking shows its bill of lading,
            container count, cargo and live position on the map.
          </p>
          <div className="pc-empty__acts">
            <button type="button" className="pcbtn pcbtn--main" onClick={() => setAdding(true)}>
              Add shipment
            </button>
            <a className="pcbtn" href={QUOTE_URL}>
              Request a Quote
            </a>
            <a className="pcbtn" href={PRODUCTS_URL}>
              See all products
            </a>
          </div>
        </div>
      )}

      {status === "ready" && shipments.length > 0 && (
        <>
          <ShipmentFilters bucket={bucket} mode={mode} onBucketChange={setBucket} onModeChange={setMode} />

          {/* The design shows the strip only once something is actually picked. */}
          {picking && pickedVisible.length > 0 && (
            <div className="pcsh__bulk">
              <span>
                {pickedVisible.length} {pickedVisible.length === 1 ? "shipment" : "shipments"} selected
              </span>
              <div className="pcsh__bulk-acts">
                <button
                  type="button"
                  className="pcbtn pcbtn--danger"
                  onClick={() => setConfirming(pickedVisible.map((shipment) => shipment.id))}
                >
                  Delete selected
                </button>
                <button type="button" className="pcbtn" onClick={() => setPicked(new Set())}>
                  Clear
                </button>
              </div>
            </div>
          )}

          <div className={cn("pcsh", { "is-picking": picking })}>
            <div className="pcsh__head">
              <label className="pcsh__pick">
                <input
                  type="checkbox"
                  checked={allPicked}
                  onChange={(event) => pickAll(event.target.checked)}
                  aria-label="Select all shipments"
                />
                <i />
              </label>
              <span />
              <span>Schedule</span>
              <span>Reference</span>
              <span>Cargo</span>
              <span>Product Details</span>
              <span>Status &amp; location</span>
              <span />
            </div>

            {visible.map((shipment) => (
              <ShipmentRow
                key={shipment.id}
                shipment={shipment}
                picking={picking}
                picked={picked.has(shipment.id)}
                onPick={pick}
                onOpen={openShipment}
                menuOpen={menu?.shipment.id === shipment.id}
                onMenu={(row, anchor) =>
                  setMenu((current) => (current?.anchor === anchor ? null : { shipment: row, anchor }))
                }
              />
            ))}

            {visible.length === 0 && <div className="pcsh__none">No shipments match this filter.</div>}
          </div>
        </>
      )}

      {menu !== null && (
        <ShipmentRowMenu
          anchor={menu.anchor}
          trackingUrl={trackingUrl(menu.shipment)}
          onAction={(action) => runAction(action, menu.shipment)}
          onClose={closeMenu}
        />
      )}

      {confirming !== null && (
        <Modal
          title={confirming.length === 1 ? "Remove this shipment?" : "Remove selected shipments?"}
          onClose={() => setConfirming(null)}
          actions={
            <>
              <button type="button" className="pcbtn pcbtn--danger" onClick={() => void confirmRemove()}>
                Remove
              </button>
              <button type="button" className="pcbtn" onClick={() => setConfirming(null)}>
                Cancel
              </button>
            </>
          }
        >
          <p className="pcmodal__t">
            <b>
              {confirming.length} {confirming.length === 1 ? "shipment" : "shipments"}
            </b>{" "}
            will stop being tracked and disappear from this list, for everyone in your company. You can add them again
            by number at any time.
          </p>
        </Modal>
      )}

      {adding && (
        <AddShipmentModal
          onClose={() => setAdding(false)}
          onAdded={onAdd}
          onLimitReached={() => {
            setAdding(false);
            setLimitReached(true);
          }}
          onOutOfCredits={(denial) => {
            setAdding(false);
            setCreditDenial(denial);
          }}
          onSessionLost={onSessionLost}
        />
      )}

      {creditDenial !== null && (
        <OutOfCreditsModal denial={creditDenial} onBuy={onBuyCredits} onClose={() => setCreditDenial(null)} />
      )}

      {limitReached && (
        <Modal
          title="You have used up your tracking allowance"
          onClose={() => setLimitReached(false)}
          actions={
            <>
              <button
                type="button"
                className="pcbtn pcbtn--main"
                onClick={() => {
                  setLimitReached(false);
                  onChoosePlan();
                }}
              >
                See your usage
              </button>
              <a className="pcbtn" href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
                Talk to us
              </a>
            </>
          }
        >
          <p className="pcmodal__t">
            This month&apos;s tracking calls are spent. The shipments you already follow keep updating; adding a new one
            needs more allowance.
          </p>
        </Modal>
      )}
    </div>
  );
};
